
import { defineComponent, ref, onMounted, watch, computed } from "vue";
import { ErrorMessage, Field, FieldArray, Form } from "vee-validate";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import { TranslationService } from "@/core/services/TranslationService";
import { ICurrentUser, IUserPhoneModel } from "@/interfaces/iCurrentUser";
import { UserAPI } from "@/services/api/User.service";
import { FileAPI } from "@/services/api/File.service";
import { SiteApi } from "@/services/api/Site.service";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { useToast } from "vue-toastification";
import VZPhone from "@/components/VZPhone.vue";
import {
  ACCOUNT_LANGUAGE_LOOKUP,
  ACCOUNT_TIMEZONE_LOOKUP,
  ACCOUNT_COUNTRY_LOOKUP,
} from "./accountDataTableOptions";
import { getLookups } from "@/services/api/Lookup.service";
import * as Yup from "yup";
import { Actions } from "@/store/enums/StoreEnums";
import ImageInput from "@/components/image/ImageInput.vue";
import { getMaxLengthErrorMessage } from "@/helpers/data-format";
export default defineComponent({
  name: "accountsettings",
  components: {
    Form,
    Field,
    ErrorMessage,
    FieldArray,
    ImageInput,
    VZPhone,
  },
  setup() {
    const store = useStore();
    const toast = useToast();
    const t = function (key: string) {
      return TranslationService.translate(key);
    };
    const editAccountSettingsMessage = t(
      "pages.user.accountSettingsUpdatedMessage"
    );
    const activeTab = ref("generalDetail");
    const updatePasswordMessage = t("pages.user.updatePasswordMessage");
    const addContactsMessage = t("pages.user.addContactsMessage");
    const timeZoneOptions = ref<Array<{ label: any; value: string }>>([]);
    const languageOptions = ref<Array<{ label: any; value: string }>>([]);
    const countryOptions = ref<Array<{ label: any; value: string }>>([]);
    const stateOptions = ref<Array<{ label: any; value: string }>>([]);
    const cityOptions = ref<Array<{ label: any; value: string }>>([]);
    const firstName = ref<string>("");
    const lastName = ref<string>("");
    const email = ref<string>("");
    const language = ref<string>("");
    const timeZone = ref<string>("");
    const confirmPassword = ref<string>("");
    const currentPassword = ref<string>("");
    const phoneNumber = ref<string>("");
    const type = ref<string>("");
    const country = ref<string>("");
    const state = ref<string>("");
    const city = ref<string>("");
    const btnLoading = ref(false);
    const myAddressModel = ref({
      street: "",
      street2: "",
      postalCode: "",
      country: "",
      state: "",
      city: "",
    });
    const street = ref<string>("");
    const postalCode = ref<string>("");
    const webId = ref<number>();
    const defaultPhone = ref<number>();
    const clientId = ref<number>();
    const phoneModel = ref<IUserPhoneModel[]>();
    const trigerInputFile = ref<boolean>(false);
    const changePasswordFormRef = ref<null | HTMLFormElement>(null);
    const updateTheme = (theme: string) => {
      store.commit("setTheme", theme);
      if (theme === "theme-black") {
        store.state.ConfigModule.config.aside.theme = "dark";
      } else if (theme === "theme-white") {
        store.state.ConfigModule.config.aside.theme = "light";
      } else {
        store.state.ConfigModule.config.aside.theme = "dark";
      }
    };
    const generalInformationValidation = Yup.object().shape({
      firstName: Yup.string()
        .max(30, getMaxLengthErrorMessage(t("common.firstName"), 30))
        .required(t("common.firstName") + " " + t("common.isRequired"))
        .matches(/^[A-Za-z][A-Za-z\s]*$/, {
          message: t("validation.notStartWithNum"),
          excludeEmptyString: true,
        }),
      lastName: Yup.string()
        .max(30, getMaxLengthErrorMessage(t("common.lastName"), 30))
        .required(t("common.lastName") + " " + t("common.isRequired"))
        .matches(/^[A-Za-z][A-Za-z\s]*$/, {
          message: t("validation.notStartWithNum"),
          excludeEmptyString: true,
        }),
    });
    const changePasswordValidation = Yup.object().shape({
      currentPassword: Yup.string()
        .max(30, getMaxLengthErrorMessage(t("pages.user.currentPassword"), 30))
        .required(
          t("pages.user.currentPassword") + " " + t("common.isRequired")
        ),
      newPassword: Yup.string()
        .max(30, getMaxLengthErrorMessage(t("pages.user.newPassword"), 30))
        .matches(
          /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%&*()]).{8,}/,
          t("validation.passwordValidation")
        )
        .required(t("pages.user.newPassword") + " " + t("common.isRequired")),
      confirmPassword: Yup.string()
        .max(30, getMaxLengthErrorMessage(t("pages.user.confirmPassword"), 30))
        .required(
          t("pages.user.confirmPassword") + " " + t("common.isRequired")
        )
        .oneOf([Yup.ref("newPassword"), null], "Password does not match"),
    });
    const cityMessage = `${t("pages.user.city")} ${t("common.isRequired")}`;
    const stateMessage = `${t("pages.user.state")} ${t("common.isRequired")}`;
    const contactDetailsValidation = Yup.object().shape({
      phoneModel: Yup.array().of(
        Yup.object().shape({
          phoneNumber: Yup.string().label(t("pages.user.phoneNumber")),
        })
      ),
      defaultPhone: Yup.string().label(t("common.defaultPhone")),
      // country: Yup.string().required("Country is required"),
      street: Yup.string()
        .max(100, getMaxLengthErrorMessage(t("pages.user.addressLine1"), 100))
        .required(t("pages.user.addressLine1") + " " + t("common.isRequired")),
      street2: Yup.string().max(
        100,
        getMaxLengthErrorMessage(t("pages.user.addressLine2"), 100)
      ),
      city: Yup.string().when([], {
        is: () => cityOptions.value.length === 0,
        then: (schema) => schema,
        otherwise: (schema) => schema.required(cityMessage),
      }),
      country: Yup.string().required(
        t("pages.user.country") + " " + t("common.isRequired")
      ),
      state: Yup.string().when([], {
        is: () => stateOptions.value.length === 0,
        then: (schema) => schema,
        otherwise: (schema) => schema.required(stateMessage),
      }),
      postalCode: Yup.string()
        .max(10, getMaxLengthErrorMessage(t("pages.user.zip"), 10))
        .required(t("pages.user.zip") + " " + t("common.isRequired")),
    });
    const currentUser = computed(() => {
      return store.getters.currentUser as ICurrentUser;
    });
    const initialValues = computed(() => {
      return {
        phoneModel: phoneModel?.value ? [...phoneModel?.value] : [],
        country: country.value || myAddressModel.value.country,
        state: state.value || myAddressModel.value.state,
        city: city.value || myAddressModel.value.city,
        street:
          myAddressModel.value.street ||
          currentUser.value?.addressModel?.street ||
          "",
        street2:
          myAddressModel.value.street2 ||
          currentUser.value?.addressModel?.street2 ||
          "",
        postalCode:
          myAddressModel.value.postalCode ||
          currentUser.value?.addressModel?.postalCode ||
          "",
      };
    });
    const handleResetAccount = async () => {
      country.value = myAddressModel.value.country;
      await loadState(myAddressModel.value.country, true);
      if (stateOptions.value.length > 0) {
        const selectedState = stateOptions.value.find(
          (option) => option.label === myAddressModel.value.state
        );
        if (selectedState) {
          state.value = selectedState.label;
          await loadCity(selectedState.value);
          city.value = myAddressModel.value.city;
        } else {
          state.value = "";
          city.value = "";
        }
      } else {
        state.value = "";
        city.value = "";
      }
    };
    const resetLangAndTime = () => {
      firstName.value = currentUser?.value.firstName;
      lastName.value = currentUser?.value.lastName;
      email.value = currentUser?.value.email;
      timeZone.value = currentUser?.value.timeZone;
      language.value = currentUser?.value.language;
    };
    const updateAccountSettings = async (reqData) => {
      btnLoading.value = true;
      reqData = {
        ...reqData,
        firstName: firstName.value,
        lastName: lastName.value,
        email: email.value,
        language: language.value,
        timeZone: timeZone.value,
        clientId: clientId.value,
        startDate: currentUser.value.startDate,
        endDate: currentUser.value.endDate,
        birthday: currentUser.value.birthday,
      };
      await UserAPI.getInstance()
        .updateAccount(reqData, webId.value)
        .then(() => {
          toast.success(editAccountSettingsMessage);
        })
        .catch(() => {
          btnLoading.value = false;
        })
        .finally(() => {
          btnLoading.value = false;
        });
    };
    const changePassword = async (reqData) => {
      reqData = {
        password: confirmPassword.value,
        firstName: firstName.value,
        lastName: lastName.value,
        email: email.value,
        language: language.value,
        timeZone: timeZone.value,
        clientId: clientId.value,
        currentPassword: currentPassword.value,
        startDate: currentUser.value.startDate,
        endDate: currentUser.value.endDate,
        birthday: currentUser.value.birthday,
      };

      await UserAPI.getInstance()
        .updateAccount(reqData, webId.value)
        .then(async () => {
          toast.success(updatePasswordMessage);
          await store.dispatch(Actions.UPDATE_USER);
          discard();
        })
        .catch(() => {
          //
        })
        .finally(() => {
          //
        });
    };
    const addContactDetails = async (reqData) => {
      try {
        btnLoading.value = true;

        const selectedCountry = countryOptions.value.find(
          (c) => c.value === country.value
        );

        const addressModel = {
          street: reqData.street,
          street2: reqData.street2,
          postalCode: reqData.postalCode,
          country: selectedCountry ? selectedCountry.label : country.value,
          state: stateOptions.value.length === 0 ? "" : reqData.state,
          city: cityOptions.value.length === 0 ? "" : reqData.city,
        };

        const phoneModel = reqData.phoneModel.map((phone, index) => ({
          phoneNumber: phone?.phoneNumber?.replace(/[^\d+]/g, ""),
          type: phone.type,
          webId: phone.webId,
          defaultPhone: reqData.defaultPhone === index,
        }));

        reqData = { addressModel };

        await UserAPI.getInstance().addContactDetail(reqData);
        await UserAPI.getInstance().updatePhoneModel(
          currentUser.value.webId,
          phoneModel
        );
        await store.dispatch(Actions.UPDATE_USER);
        toast.success(addContactsMessage);

        myAddressModel.value = {
          street: reqData.street,
          street2: reqData.street2,
          postalCode: reqData.postalCode,
          country: selectedCountry ? selectedCountry.value : country.value,
          state: stateOptions.value.length === 0 ? "" : reqData.state,
          city: cityOptions.value.length === 0 ? "" : reqData.city,
        };

        // Reload state and city options
        if (selectedCountry?.value) {
          await loadState(selectedCountry.value, true); // Load states
          const selectedState = stateOptions.value.find(
            (c) => c.label.toLowerCase() === reqData.state.toLowerCase()
          );
          if (selectedState?.value) {
            await loadCity(selectedState.value); // Load cities
          }
        }

        country.value = selectedCountry ? selectedCountry.value : country.value;
        state.value = stateOptions.value.length === 0 ? "" : reqData.state;
        city.value = cityOptions.value.length === 0 ? "" : reqData.city;

        btnLoading.value = false;
      } catch (e) {
        btnLoading.value = false;
        console.error("Error in addContactDetails:", e);
      }
    };

    const discard = () => {
      changePasswordFormRef?.value?.resetForm();
    };
    const selectedImage = ref();

    const removePicture = async () => {
      if (selectedImage.value === false) {
        return;
      }
      try {
        selectedImage.value = false;
        const response = await FileAPI.getInstance().deleteImage(
          currentUser.value.fileId
        );
        if (response.status === 200) {
          toast.success("Profile picture has been removed successfully");
        }
      } catch (error) {
        console.log(error);
      } finally {
        //
      }
    };
    const onImageChange = (newImage) => {
      selectedImage.value = newImage;
    };
    onMounted(async () => {
      setCurrentPageBreadcrumbs(t("pages.user.accountSettings"), [
        "Home",
        "Account Settings",
      ]);

      const lookupOptions = await Promise.all([
        getLookups(ACCOUNT_LANGUAGE_LOOKUP.url),
        getLookups(ACCOUNT_TIMEZONE_LOOKUP.url),
        getLookups(ACCOUNT_COUNTRY_LOOKUP.url),
      ]);

      languageOptions.value = lookupOptions[0]?.map((item) => ({
        label: item.text,
        value: item.value,
      }));
      timeZoneOptions.value = lookupOptions[1]?.map((item) => ({
        label: item.text,
        value: item.value,
      }));
      countryOptions.value = [];

      await SiteApi.getInstance()
        .getCountries()
        .then(async (data) => {
          countryOptions.value = data.map((item) => ({
            label: item.text,
            value: item.value,
          }));

          if (currentUser.value?.addressModel) {
            // Try matching by value or label to handle inconsistencies
            const storedCountry = currentUser.value.addressModel.country ?? "";
            const selectedCountry = countryOptions.value.find(
              (c) =>
                c.value.toLowerCase() === storedCountry.toLowerCase() ||
                c.label.toLowerCase() === storedCountry.toLowerCase()
            );

            if (selectedCountry?.value) {
              country.value = selectedCountry.value;
              myAddressModel.value.country = selectedCountry.value;

              // Load states and set state value
              await loadState(selectedCountry.value, true);
              const storedState = currentUser.value.addressModel.state ?? "";
              const selectedState = stateOptions.value.find(
                (c) => c.label.toLowerCase() === storedState.toLowerCase()
              );

              if (selectedState?.value) {
                myAddressModel.value.state = selectedState.label;
                state.value = selectedState.label;

                // Load cities and set city value
                await loadCity(selectedState.value);
                const storedCity = currentUser.value.addressModel.city ?? "";
                const selectedCity = cityOptions.value.find(
                  (c) => c.label.toLowerCase() === storedCity.toLowerCase()
                );

                if (selectedCity?.value) {
                  myAddressModel.value.city = selectedCity.label;
                  city.value = selectedCity.label;
                }
              }

              // Set other address fields
              myAddressModel.value.street =
                currentUser.value.addressModel.street ?? "";
              myAddressModel.value.street2 =
                currentUser.value.addressModel.street2 ?? "";
              myAddressModel.value.postalCode =
                currentUser.value.addressModel.postalCode ?? "";
            }
          }
        })
        .catch((err) => {
          console.error("Error loading countries:", err);
        });
    });
    watch(
      currentUser,
      async (val) => {
        if (val) {
          firstName.value = val.firstName;
          lastName.value = val.lastName;
          email.value = val.email;
          language.value = val.language;
          timeZone.value = val.timeZone;
          webId.value = val.webId;
          clientId.value = val.clientId;
          defaultPhone.value =
            val?.userPhoneModels?.findIndex(
              (phone) => phone?.phoneModel?.defaultPhone
            ) ?? 0;
          phoneModel.value = val?.userPhoneModels?.map((phone) => ({
            phoneNumber: phone.phoneModel?.phoneNumber ?? "",
            type: phone.phoneModel?.type ?? "",
            webId: phone.phoneModel?.webId,
            defaultPhone: phone.phoneModel?.defaultPhone ?? false,
          }));

          // Initialize address fields
          if (val.addressModel && countryOptions.value.length > 0) {
            const storedCountry = val.addressModel.country ?? "";
            const selectedCountry = countryOptions.value.find(
              (c) =>
                c.value.toLowerCase() === storedCountry.toLowerCase() ||
                c.label.toLowerCase() === storedCountry.toLowerCase()
            );

            if (selectedCountry?.value) {
              country.value = selectedCountry.value;
              myAddressModel.value.country = selectedCountry.value;

              await loadState(selectedCountry.value, true);
              const storedState = val.addressModel.state ?? "";
              const selectedState = stateOptions.value.find(
                (c) => c.label.toLowerCase() === storedState.toLowerCase()
              );

              if (selectedState?.value) {
                myAddressModel.value.state = selectedState.label;
                state.value = selectedState.label;

                await loadCity(selectedState.value);
                const storedCity = val.addressModel.city ?? "";
                const selectedCity = cityOptions.value.find(
                  (c) => c.label.toLowerCase() === storedCity.toLowerCase()
                );

                if (selectedCity?.value) {
                  myAddressModel.value.city = selectedCity.label;
                  city.value = selectedCity.label;
                }
              }

              myAddressModel.value.street = val.addressModel.street ?? "";
              myAddressModel.value.street2 = val.addressModel.street2 ?? "";
              myAddressModel.value.postalCode =
                val.addressModel.postalCode ?? "";
            }
          }
        }
      },
      { immediate: true }
    );

    watch(activeTab, async (newTab) => {
      if (newTab === "contactDetail" && currentUser.value?.addressModel) {
        const storedCountry = currentUser.value.addressModel.country ?? "";
        const selectedCountry = countryOptions.value.find(
          (c) =>
            c.value.toLowerCase() === storedCountry.toLowerCase() ||
            c.label.toLowerCase() === storedCountry.toLowerCase()
        );

        if (selectedCountry?.value) {
          country.value = selectedCountry.value;
          myAddressModel.value.country = selectedCountry.value;

          await loadState(selectedCountry.value, true);
          const storedState = currentUser.value.addressModel.state ?? "";
          const selectedState = stateOptions.value.find(
            (c) => c.label.toLowerCase() === storedState.toLowerCase()
          );

          if (selectedState?.value) {
            myAddressModel.value.state = selectedState.label;
            state.value = selectedState.label;

            await loadCity(selectedState.value);
            const storedCity = currentUser.value.addressModel.city ?? "";
            const selectedCity = cityOptions.value.find(
              (c) => c.label.toLowerCase() === storedCity.toLowerCase()
            );

            if (selectedCity?.value) {
              myAddressModel.value.city = selectedCity.label;
              city.value = selectedCity.label;
            }
          }

          myAddressModel.value.street =
            currentUser.value.addressModel.street ?? "";
          myAddressModel.value.street2 =
            currentUser.value.addressModel.street2 ?? "";
          myAddressModel.value.postalCode =
            currentUser.value.addressModel.postalCode ?? "";
        }
      }
    });
    const validationErrors = ref([]) as any;
    const handleValidation = (value, index) => {
      validationErrors.value[index] = value;
    };

    const changeCountry = async (value) => {
      stateOptions.value = [];
      cityOptions.value = [];
      state.value = "";
      city.value = "";
      const selectedCountry = countryOptions.value.find(
        (c) => c.value === value
      );
      if (selectedCountry?.value) {
        await loadState(selectedCountry.value, false);
      }
    };

    const changeState = async (value) => {
      cityOptions.value = [];
      city.value = "";
      const selectedState = stateOptions.value.find((c) => c.label === value);
      if (selectedState?.value) {
        await loadCity(selectedState.value);
      }
    };
    const loadState = async (value, preserveValues) => {
      let newStateOptions = [];
      if (!preserveValues) {
        state.value = "";
        city.value = "";
      }
      if (value) {
        try {
          const data = await SiteApi.getInstance().getStates(value);
          newStateOptions = data.map((item) => ({
            label: item.text,
            value: item.value,
          }));
          stateOptions.value = newStateOptions;
          if (newStateOptions.length === 0) {
            cityOptions.value = [];
            city.value = "";
          }
        } catch (err) {
          console.error("Error loading states:", err);
          stateOptions.value = [];
          cityOptions.value = [];
          city.value = "";
        }
      } else {
        stateOptions.value = [];
        cityOptions.value = [];
        city.value = "";
      }
    };

    const loadCity = async (value) => {
      let newCityOptions = [];
      if (value) {
        try {
          const data = await SiteApi.getInstance().getCities(value);
          newCityOptions = data.map((item) => ({
            label: item.text,
            value: item.value,
          }));
          cityOptions.value = newCityOptions;
        } catch (err) {
          console.error("Error loading cities:", err);
          cityOptions.value = [];
        }
      } else {
        cityOptions.value = [];
      }
    };

    return {
      t,
      updateAccountSettings,
      changePassword,
      addContactDetails,
      generalInformationValidation,
      changePasswordValidation,
      contactDetailsValidation,
      languageOptions,
      timeZoneOptions,
      countryOptions,
      confirmPassword,
      currentPassword,
      phoneNumber,
      type,
      city,
      state,
      country,
      postalCode,
      street,
      firstName,
      lastName,
      email,
      language,
      timeZone,
      webId,
      clientId,
      updateTheme,
      status,
      initialValues,
      currentUser,
      changePasswordFormRef,
      defaultPhone,
      selectedImage,
      onImageChange,
      trigerInputFile,
      removePicture,
      validationErrors,
      handleValidation,
      stateOptions,
      cityOptions,
      loadState,
      loadCity,
      changeCountry,
      changeState,
      myAddressModel,
      btnLoading,
      handleResetAccount,
      resetLangAndTime,
      activeTab,
      cityMessage,
      stateMessage,
    };
  },
});
